import React, { useState, useEffect } from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchSelect, TouchFormGroup } from '../ui/TouchForm';
import searchService from '../../services/SearchService';

const SearchFilters = ({ 
  onFiltersChange, 
  initialFilters = {},
  showLocationFilter = false,
  userLocation = null,
  className = ''
}) => {
  const [filters, setFilters] = useState({
    safetyRating: '',
    locationType: '',
    category: '',
    radius: '1000',
    hasReviews: '',
    sortBy: 'relevance',
    ...initialFilters
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [locationTypes, setLocationTypes] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Load location types and categories
    setLocationTypes(searchService.getLocationTypes());
    setCategories(searchService.getLocationCategories());
  }, []);

  useEffect(() => {
    // Notify parent of filter changes
    onFiltersChange && onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetFilters = () => {
    const defaultFilters = {
      safetyRating: '',
      locationType: '',
      category: '',
      radius: '1000',
      hasReviews: '',
      sortBy: 'relevance'
    };
    setFilters(defaultFilters);
  };

  const hasActiveFilters = () => {
    return filters.safetyRating || 
           filters.locationType || 
           filters.category || 
           filters.hasReviews || 
           (showLocationFilter && filters.radius !== '1000') ||
           filters.sortBy !== 'relevance';
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.safetyRating) count++;
    if (filters.locationType) count++;
    if (filters.category) count++;
    if (filters.hasReviews) count++;
    if (showLocationFilter && filters.radius !== '1000') count++;
    if (filters.sortBy !== 'relevance') count++;
    return count;
  };

  return (
    <div className={`search-filters ${className}`}>
      {/* Basic Filters */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        {/* Safety Rating Filter */}
        <div style={{ minWidth: '140px', flex: '1' }}>
          <TouchSelect
            value={filters.safetyRating}
            onChange={(e) => handleFilterChange('safetyRating', e.target.value)}
            style={{ fontSize: '14px' }}
          >
            <option value="">Any Safety Rating</option>
            <option value="4.5">⭐ 4.5+ Very Safe</option>
            <option value="4.0">⭐ 4.0+ Safe</option>
            <option value="3.5">⭐ 3.5+ Good</option>
            <option value="3.0">⭐ 3.0+ Moderate</option>
            <option value="2.0">⭐ 2.0+ Basic</option>
          </TouchSelect>
        </div>

        {/* Location Type Filter */}
        <div style={{ minWidth: '140px', flex: '1' }}>
          <TouchSelect
            value={filters.locationType}
            onChange={(e) => handleFilterChange('locationType', e.target.value)}
            style={{ fontSize: '14px' }}
          >
            <option value="">All Location Types</option>
            {locationTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.icon} {type.label}
              </option>
            ))}
          </TouchSelect>
        </div>

        {/* Sort By */}
        <div style={{ minWidth: '120px', flex: '1' }}>
          <TouchSelect
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            style={{ fontSize: '14px' }}
          >
            <option value="relevance">Most Relevant</option>
            <option value="rating">Highest Rated</option>
            <option value="reviews">Most Reviews</option>
            <option value="distance">Nearest</option>
            <option value="name">Alphabetical</option>
          </TouchSelect>
        </div>

        {/* Advanced/Reset Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <TouchButton
            onClick={() => setShowAdvanced(!showAdvanced)}
            variant="outline"
            size="small"
            style={{ 
              fontSize: '12px',
              padding: '8px 12px',
              position: 'relative'
            }}
          >
            {showAdvanced ? '📋 Less' : '⚙️ More'}
            {hasActiveFilters() && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                backgroundColor: '#2196F3',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '10px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {getActiveFilterCount()}
              </span>
            )}
          </TouchButton>
          
          {hasActiveFilters() && (
            <TouchButton
              onClick={resetFilters}
              variant="ghost"
              size="small"
              style={{ fontSize: '12px', color: '#666' }}
            >
              Clear
            </TouchButton>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div style={{
          marginTop: '12px',
          padding: '16px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            {/* Category Filter */}
            <TouchFormGroup>
              <TouchSelect
                label="Category"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.icon} {category.label}
                  </option>
                ))}
              </TouchSelect>
            </TouchFormGroup>

            {/* Distance Radius (if location available) */}
            {showLocationFilter && userLocation && (
              <TouchFormGroup>
                <TouchSelect
                  label="Distance"
                  value={filters.radius}
                  onChange={(e) => handleFilterChange('radius', e.target.value)}
                >
                  <option value="500">Within 500m</option>
                  <option value="1000">Within 1km</option>
                  <option value="2000">Within 2km</option>
                  <option value="5000">Within 5km</option>
                  <option value="10000">Within 10km</option>
                  <option value="25000">Within 25km</option>
                </TouchSelect>
              </TouchFormGroup>
            )}

            {/* Has Reviews Filter */}
            <TouchFormGroup>
              <TouchSelect
                label="Reviews"
                value={filters.hasReviews}
                onChange={(e) => handleFilterChange('hasReviews', e.target.value)}
              >
                <option value="">Any Review Count</option>
                <option value="true">Has Reviews</option>
                <option value="false">No Reviews</option>
              </TouchSelect>
            </TouchFormGroup>
          </div>

          {/* Quick Filter Buttons */}
          <div style={{ marginTop: '16px' }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: '600', 
              marginBottom: '8px',
              color: '#666'
            }}>
              Quick Filters:
            </div>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px' 
            }}>
              <QuickFilterButton
                active={filters.locationType === 'restaurant'}
                onClick={() => handleFilterChange('locationType', 
                  filters.locationType === 'restaurant' ? '' : 'restaurant'
                )}
              >
                🍽️ Restaurants
              </QuickFilterButton>
              
              <QuickFilterButton
                active={filters.locationType === 'hotel'}
                onClick={() => handleFilterChange('locationType', 
                  filters.locationType === 'hotel' ? '' : 'hotel'
                )}
              >
                🏨 Hotels
              </QuickFilterButton>
              
              <QuickFilterButton
                active={filters.locationType === 'attraction'}
                onClick={() => handleFilterChange('locationType', 
                  filters.locationType === 'attraction' ? '' : 'attraction'
                )}
              >
                🗼 Attractions
              </QuickFilterButton>
              
              <QuickFilterButton
                active={filters.locationType === 'transport'}
                onClick={() => handleFilterChange('locationType', 
                  filters.locationType === 'transport' ? '' : 'transport'
                )}
              >
                🚇 Transport
              </QuickFilterButton>
              
              <QuickFilterButton
                active={filters.safetyRating === '4.0'}
                onClick={() => handleFilterChange('safetyRating', 
                  filters.safetyRating === '4.0' ? '' : '4.0'
                )}
              >
                ⭐ High Safety
              </QuickFilterButton>
              
              <QuickFilterButton
                active={filters.hasReviews === 'true'}
                onClick={() => handleFilterChange('hasReviews', 
                  filters.hasReviews === 'true' ? '' : 'true'
                )}
              >
                📝 Reviewed
              </QuickFilterButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const QuickFilterButton = ({ children, active, onClick, ...props }) => (
  <TouchButton
    onClick={onClick}
    variant={active ? "primary" : "outline"}
    size="small"
    style={{
      fontSize: '12px',
      padding: '6px 12px',
      backgroundColor: active ? '#2196F3' : 'transparent',
      borderColor: active ? '#2196F3' : '#ddd',
      color: active ? 'white' : '#666',
      transition: 'all 150ms ease'
    }}
    {...props}
  >
    {children}
  </TouchButton>
);

export default SearchFilters;